import { Link } from "react-router-dom";
import { useMemo } from "react";
import { ARTIST, ANIMATION, PAINTINGS, CARDS } from "../data/portfolio";
import { ArrowUpRight, Instagram, Linkedin } from "lucide-react";

const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

const Marquee = () => (
  <div className="overflow-hidden border-y hairline py-5 my-24">
    <div className="ticker whitespace-nowrap flex gap-12 font-serif italic text-3xl md:text-5xl text-[var(--bone)]">
      {Array.from({ length: 6 }).map((_, i) => (
        <span key={i} className="flex items-center gap-12">
          Warner Bros. <span className="text-[var(--ember)]">✦</span>
          Klaus — SPA Studios <span className="text-[var(--ember)]">✦</span>
          Cinesite · Animal Farm <span className="text-[var(--ember)]">✦</span>
          DreamWorks <span className="text-[var(--ember)]">✦</span>
          The Line Studio <span className="text-[var(--ember)]">✦</span>
        </span>
      ))}
    </div>
  </div>
);

export default function Home() {
  const pillars = useMemo(() => [
    { to:"/animation", title:"Animation", count:ANIMATION.length, src:pickRandom(ANIMATION).src, desc:"Vis dev, color keys, background painting & layout for feature animation." },
    { to:"/paintings", title:"Paintings", count:PAINTINGS.length, src:pickRandom(PAINTINGS).src, desc:"Personal paintings — studies, film homages and explorations of light." },
    { to:"/cards", title:"Cards", count:CARDS.length, src:pickRandom(CARDS).src, desc:"Illustrations for Disney Lorcana — characters and locations." },
  ], []);
  return (
    <div>
      {/* Hero */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-10 pt-10 pb-24 md:pt-20 md:pb-32">
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 lg:col-span-8 reveal">
            <h1 className="font-serif text-7xl md:text-9xl lg:text-[12rem] leading-[0.85] text-[var(--paper)] italic" data-testid="hero-eyebrow">
              Portfolio
            </h1>
            <div className="text-[10px] uppercase tracking-[0.55em] text-[var(--ember)] mt-5">2016 / 2025</div>
          </div>
          <div className="col-span-12 lg:col-span-4 reveal" style={{ transitionDelay: "200ms" }}>
            <p className="text-[var(--bone)] text-lg md:text-xl leading-relaxed max-w-md">
              {ARTIST.name} is an Art Director and Visual Development artist for animation feature film — building mood,
              palette and place for stories that move people.
            </p>
            <div className="mt-8 flex flex-col gap-3 text-sm uppercase tracking-[0.3em]">
              <Link to="/animation" data-testid="cta-animation" className="link-underline w-fit text-[var(--paper)]">
                Animation Work →
              </Link>
              <Link to="/contact" data-testid="cta-contact" className="link-underline w-fit text-[var(--paper)]">
                Commission a Piece →
              </Link>
            </div>
          </div>
        </div>

        {/* Hero image */}
        <div className="mt-20 reveal" style={{ transitionDelay: "300ms" }}>
          <div className="overflow-hidden bg-[var(--ink-2)]">
            <img
              src="https://customer-assets.emergentagent.com/job_gallery-works/artifacts/rbm8duhi_image.png"
              alt="Featured visual development piece by Sergio Mancinelli"
              className="w-full h-auto block"
              data-testid="hero-image"
            />
          </div>
          <div className="mt-4 flex justify-between text-xs uppercase tracking-[0.25em] text-[var(--muted)]">
            <span>Featured · Visual Development</span>
            <span className="font-mono">N°01 / 2024</span>
          </div>
        </div>
      </section>

      <Marquee/>

      {/* Three pillars */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6">
          {pillars.map((p, i) => (
            <Link key={p.to} to={p.to} data-testid={`pillar-${p.title.toLowerCase()}`} className="group reveal" style={{ transitionDelay: `${i*120}ms` }}>
              <div className="aspect-[4/5] overflow-hidden bg-[var(--ink-2)]">
                <img src={p.src} alt={p.title} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"/>
              </div>
              <div className="mt-5 flex items-start justify-between">
                <div>
                  <div className="font-serif text-3xl md:text-4xl text-[var(--paper)] group-hover:text-[var(--ember)] transition-colors">{p.title}</div>
                  <div className="text-xs uppercase tracking-[0.3em] text-[var(--muted)] mt-2">{p.count} works</div>
                </div>
                <ArrowUpRight className="text-[var(--paper)] group-hover:text-[var(--ember)] group-hover:rotate-45 transition-all duration-500" size={28}/>
              </div>
              <p className="text-[var(--bone)] text-sm leading-relaxed mt-3 max-w-sm">{p.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Social footer */}
      <footer className="max-w-[1600px] mx-auto px-6 lg:px-10 pb-20 pt-4">
        <div className="border-t hairline pt-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
            © {new Date().getFullYear()} Sergio Mancinelli
          </div>
          <div className="flex items-center gap-3" data-testid="social-links">
            <a
              href="https://www.instagram.com/sergio__mancinelli/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="social-instagram"
              className="w-11 h-11 flex items-center justify-center border hairline text-[var(--bone)] hover:text-[var(--ember)] hover:border-[var(--ember)] transition-colors duration-500"
            >
              <Instagram size={18}/>
            </a>
            <a
              href="https://www.linkedin.com/in/sergiomancinelliillustrator/?locale=en"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              data-testid="social-linkedin"
              className="w-11 h-11 flex items-center justify-center border hairline text-[var(--bone)] hover:text-[var(--ember)] hover:border-[var(--ember)] transition-colors duration-500"
            >
              <Linkedin size={18}/>
            </a>
            <a
              href="https://www.imdb.com/it/name/nm3644585/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="IMDb"
              data-testid="social-imdb"
              className="h-11 px-4 flex items-center justify-center border hairline text-[var(--bone)] hover:text-[var(--ember)] hover:border-[var(--ember)] transition-colors duration-500 font-bold tracking-wider text-xs"
            >
              IMDb
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
